import { LocalStorage } from 'quasar'


interface FeatureValues {
  blockingThreshold: number
  cameraToolingUrl: string
  configurableLabelerPageSize: boolean
  developerMode: boolean
  emulatorCameraConfigurations: boolean
  googleTagManager: boolean
  hotjar: boolean
  importAlgoConnectedPlatform: boolean
  inferenceCenterMode: number
  resetBranding: boolean
  shareDatasetsAcrossProjects: boolean
  shareModelsAcrossProjects: boolean
  showModelAlgoVersion: boolean
  showPlatformToPlatformConnection: boolean
  showPlugins: boolean
  showRecordings: boolean
}

type WindowKeys = keyof Window['features']
export type FeatureKeys = keyof FeatureValues

type FeatureValueType = typeof Boolean | typeof Number | typeof String
const MapFeatures: [WindowKeys, FeatureKeys, FeatureValueType][] = [
  ['BLOCKING_THRESHOLD', 'blockingThreshold', Number],
  ['CAMERA_TOOLING_URL', 'cameraToolingUrl', String],
  ['CONFIGURABLE_LABELER_PAGE_SIZE', 'configurableLabelerPageSize', Boolean],
  ['DEVELOPER_MODE', 'developerMode', Boolean],
  ['EMULATOR_CAMERA_CONFIGURATIONS', 'emulatorCameraConfigurations', Boolean],
  ['GOOGLE_TAG_MANAGER', 'googleTagManager', Boolean],
  ['HOTJAR', 'hotjar', Boolean],
  ['IMPORT_ALGO_CONNECTED_PLATFORM', 'importAlgoConnectedPlatform', Boolean],
  ['INFERENCE_CENTER_MODE', 'inferenceCenterMode', Number],
  ['RESET_BRANDING', 'resetBranding', Boolean],
  ['SHARE_DATASETS_ACROSS_PROJECTS', 'shareDatasetsAcrossProjects', Boolean],
  ['SHARE_MODELS_ACROSS_PROJECTS', 'shareModelsAcrossProjects', Boolean],
  ['SHOW_MODEL_ALGO_VERSION', 'showModelAlgoVersion', Boolean],
  ['SHOW_PLATFORM_TO_PLATFORM_CONNECTION', 'showPlatformToPlatformConnection', Boolean],
  ['SHOW_PLUGINS', 'showPlugins', Boolean],
  ['SHOW_RECORDINGS', 'showRecordings', Boolean],
]

const LOCAL_STORAGE_KEY = 'featureFlags'

export enum InferenceCenterMode {
  NoExampleStreamDependency = 0,
  OnlyExampleStreamDependency = 1,
  All = 2,
}

export class Features implements FeatureValues {
  public blockingThreshold: number = 1000
  public cameraToolingUrl: string = ''
  public configurableLabelerPageSize: boolean = false
  public developerMode: boolean = false
  public emulatorCameraConfigurations: boolean = false
  public googleTagManager: boolean = false
  public hotjar: boolean = false
  public importAlgoConnectedPlatform: boolean = false
  public inferenceCenterMode: InferenceCenterMode = 0
  public infiniteLabeler: boolean = false
  public resetBranding: boolean = false
  public showModelAlgoVersion: boolean = false
  public shareDatasetsAcrossProjects: boolean = false
  public shareModelsAcrossProjects: boolean = false
  public showPlatformToPlatformConnection = true
  public showPlugins: boolean = false
  public showRecordings: boolean = false

  public constructor () {
    const lsParams: Partial<Record<FeatureKeys, boolean>> = LocalStorage.getItem(LOCAL_STORAGE_KEY) || {}
    const urlParams = new URLSearchParams(window.location.search)

    MapFeatures.forEach(([featureKey, key, type]) => {
      const value = this.parseValue(
        type,
        urlParams.get(key),
        lsParams[key],
        window.features[featureKey],
        process.env[featureKey],
        this[key],
      )
      this[key] = value as never
    })
  }

  public get entries (): [FeatureKeys, boolean | number | string][] {
    return MapFeatures.map<[FeatureKeys, boolean | number | string]>(([, key]) => [key, this[key]])
  }

  public reset () {
    LocalStorage.remove(LOCAL_STORAGE_KEY)
    window.location.reload()
  }

  public setValue (key: FeatureKeys, value: boolean | number) {
    const lsParams: Partial<Record<FeatureKeys, boolean | number>> = LocalStorage.getItem(LOCAL_STORAGE_KEY) || {}
    lsParams[key] = value
    LocalStorage.set(LOCAL_STORAGE_KEY, lsParams)
    window.location.reload()
  }

  private parseValue (
    type: FeatureValueType,
    ...values: (number | boolean | string | null | undefined)[]
  ): number | boolean | string {
    if (type === Boolean) {
      for (const value of values) {
        if (value == null || value === '') continue
        if (typeof value !== 'string') return !!value
        if (/^\{\{\w+\}\}$/.exec(value)) continue
        return value === 'true' || value === 'True' || value === '1'
      }

      return false
    } else if (type === Number) {
      for (const value of values) {
        if (typeof value === 'number') return value
        if (value == null || value === '') continue
        if (typeof value !== 'string') continue
        if (/^\{\{\w+\}\}$/.exec(value)) continue
        if (!/^\d+$/.test(value)) continue
        return Number(value)
      }

      return 0
    } else if (type === String) {
      for (const value of values) {
        if (typeof value === 'string') return value
        if (value == null) continue
        return String(value)
      }

      return ''
    } else {
      // unreachable
      throw new Error('unexpected type for feature flag')
    }
  }
}

export const features = new Features()
